<template>
    <v-container fluid class="white elevation-4 pt-0">
        <main-app-bar :title="$router.currentRoute.meta.title">
        </main-app-bar>
        <main-rota-settings-dialog :isOpen="settingsModal" :defaultSettings="defaultSettings" :closeModal="closeSettingsModal" @change:settings="applySettings($event)"></main-rota-settings-dialog>
        <div class="pt-3 main-rota-container">
            <div class="main-rota-head">
                <v-toolbar flat color="white" class="px-0">
                    <v-autocomplete
                        ref="roleSelect"
                        v-model="roleId"
                        :items="roleList"
                        item-text="title"
                        item-value="id"
                        dense
                        multiple
                        solo
                        lighten-1
                        hide-details
                        label="Roles"
                        :search-input.sync="searchInputAutocomplete[selectInputConts.roles]"
                        @change="roleChangeHandler(true, selectInputConts.roles)"
                        style="max-width: 250px;"
                        height="30px"
                        class="my-2 font-weight-regular"
                    >
                        <template v-slot:selection="{ item, index }">
                            <span v-if="index === 0" class="selection-text-style">{{ item.title }}</span>
                            <span
                                v-if="index === 1"
                                class="grey--text caption"
                            >(+{{ roleId.length - 1 }} others)</span>
                        </template>
                    </v-autocomplete>
                    <v-autocomplete
                        ref="employeeSelect"
                        v-model="chosenEmployee"
                        :items="employees"
                        item-text="fullName"
                        item-value="id"
                        dense
                        solo
                        lighten-1
                        hide-details
                        label="Employee"
                        @change="searchByEmployee()"
                        style="max-width: 250px;"
                        class="my-2 mx-4 font-weight-regular"
                        :loading="employeeSearchLoading"
                    >
                        <template v-slot:selection="{ item }">
                            <span style="max-width: 200px;overflow: hidden" class="selection-text-style">{{ item.fullName }}</span>
                        </template>
                    </v-autocomplete>
                    <div class="d-flex align-center justify-center">
                        <v-btn
                            :disabled="clinicsData.findIndex(clinic => clinic.loading === true) >= 0"
                            fab
                            elevation="3"
                            small
                            color="primary"
                            @click="weekNav('prev')"
                        >
                            <v-icon dark>mdi-chevron-left</v-icon>
                        </v-btn>
                        <span class="mx-4 font-weight-bold">Dates</span>
                        <v-btn
                            :disabled="clinicsData.findIndex(clinic => clinic.loading === true) >= 0"
                            fab
                            elevation="3"
                            small
                            color="primary"
                            @click="weekNav('next')"
                        >
                            <v-icon dark>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn elevation="3" fab @click="reloadMainRota" small :disabled="reloadDisabled" class="mr-4"><v-icon>mdi-refresh</v-icon></v-btn>
                    <v-btn elevation="3" fab @click="settingsModal = true" small><v-icon>mdi-cog</v-icon></v-btn>
                </v-toolbar>
                <role-colors-legend class="mx-4 my-4" :roles="roleId"/>
            </div>
            <v-row class="fill-height px-4">
                <v-col cols="12" class="pt-0">
                    <v-data-table
                        :hide-default-header="true"
                        :hide-default-footer="true"
                        item-key="name"
                        show-expand
                        dense
                        class="elevation-0 table-bordered table-wrapper"
                        v-if="roleId.length > 0"
                    >
                        <template v-slot:header>
                            <thead>
                            <tr>
                                <th rowspan="2" class="text-center font-weight-bold body-2 sticky-td sticky-td-desktop">
                                    Clinic/Day
                                </th>
                                <th v-for="day in weekDates" :key="day" class="sticky-th sticky-th--days">
                                    <div class="text-center body-2 text-uppercase font-weight-bold"> {{day | dayFormat}} </div>
                                </th>
                            </tr>
                            <tr>
                                <th v-for="index in weekDates.length" :key="'day' + index" class="sticky-th sticky-th--times">
                                    <div class="d-flex justify-space-between align-center" style="height: 30px;" >
                                        <div class="px-2 flex-grow-1 text-center font-weight-bold text--black">AM</div>
                                        <div class="px-2 flex-grow-1 text-center font-weight-bold text--black">PM</div>
                                    </div>
                                </th>
                            </tr>
                            </thead>
                        </template>
                        <template v-slot:body>
                            <tbody>
                            <template v-for="(clinic) in clinicsData">
                                <tr :key="clinic.id" @click="clinicClickHandler(clinic)" class="expandable-tr" :disabled="clinicExpandDisabled" :class="{'disabled-tr': clinicExpandDisabled, 'expanded' : clinic.isExpand}">
                                    <td class="sticky-td">
                                        <div class="d-flex align-center justify-space-between">
                                            <span class="text-uppercase">{{clinic.title}}</span> <v-btn :color="clinic.isExpand ? '#6c9d39' : ''" fab icon x-small :loading="clinic.loading" elevation="0"><v-icon :style="clinic.isExpand ? 'transform: rotateZ(180deg)' : ''" large>mdi-chevron-down</v-icon></v-btn>
                                        </div>
                                    </td>
                                    <td :colspan="weekDates.length" style="position:relative;">
                                        {{clinic.loading ? 'Loading..' : ''}}
                                        <v-progress-linear
                                            v-if="clinic.loading"
                                            indeterminate
                                            absolute
                                            height="2"
                                            :value="0"
                                            bottom
                                            color="primary"
                                        />
                                    </td>
                                </tr>
                                <tr :key="'expand' + clinic.id" class="expanded-content" v-if="clinic.isExpand && !clinic.loading">
                                    <td class="sticky-td">
                                        <span v-if="clinic.id === -1">HQ</span>
                                        <span v-else>
                                            {{typeof clinic.id === 'number' ? 'Clinic' : ''}}
                                         </span>
                                    </td>
                                    <td v-for="(day) in weekDates" :key="'td' + day">
                                        <v-lazy :options="{threshold: 1.0}">
                                            <div v-if="rotaData[day]">
                                                <main-calendar-day
                                                    :clinicData="rotaData[day]"
                                                    :type="'clinic'"
                                                    :id="clinic.id"
                                                    :openingsData="clinicOpeningsData[day].clinic[clinic.id]"
                                                />
                                            </div>
                                        </v-lazy>
                                    </td>
                                </tr>
                                <template v-if="clinic.isExpand && !clinic.loading">
                                    <tr v-for="surgery in clinic.surgeries" :key="'surgery' + surgery.id" class="expanded-content">
                                        <td class="sticky-td">
                                            {{surgery.value}}
                                        </td>
                                        <td v-for="(day) in weekDates" :key="'surgery-day' + day">
                                            <v-lazy :options="{threshold: 1.0}">
                                                <div v-if="rotaData[day]">
                                                    <main-calendar-day
                                                        :clinicData="rotaData[day]"
                                                        :type="'surgeries'"
                                                        :id="surgery.id"
                                                        :openingsData="clinicOpeningsData[day].surgeries[surgery.id]"
                                                    />
                                                </div>
                                            </v-lazy>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                            </tbody>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </div>
        <rota-transfer-form
            v-if="isApprovePermission"
            @successSubmit="successSubmit($event)"
            :selected-items="selectedRotaItems"
            :unSelectItem="unSelectItem"
            :clearSelectedList="clearSelectedList"
            :transferAction="reassignPersonalRota"
        />
        <add-rota-record
            v-if="isApprovePermission"
            @successSubmit="successSubmit($event)"
            :selected-items="selectedRotaItems"
            :unSelectItem="unSelectItem"
            :clearSelectedList="clearSelectedList"
            :transferAction="reassignPersonalRota"
        />
        <rota-note
            v-if="isPermissionForRotaNotes"
            @successSubmit="onNoteSuccess"
            :selected-items="selectedRotaItems || []"
            :transferAction="updateUserRotaNote"
        />
    </v-container>
</template>
<script>
    import MainAppBar from '@/components/MainAppBar'
    import MainCalendarDay from './components/MainCalendarDay'
    import RotaTransferForm from './components/RotaTransferForm'
    import MainRotaSettingsDialog from './components/MainRotaSettingsDialog'
    import RoleColorsLegend from './components/RoleColorsLegend'
    import moment from 'moment'
    import animateComponent from '@/common/animateComponent'
    import { getMonthName } from '@/common/date'
    import { ROTA_STATUSES } from '@/common/constants'
    import { mapActions, mapGetters } from 'vuex'
    import AddRotaRecord from './components/AddRotaRecord'
    import RotaNote from './components/RotaNote'
    import formatDates from '../../constatns/formatDates'
    import selectInputAutocomplete from '../../constatns/selectInputAutocomplete'

    const defaultSettings = () => {
        const settings = localStorage.getItem('mainRotaTableSettings')
        const defaults = {
            days: 7
        }
        if (settings) {
            return JSON.parse(settings)
        } else {
            return defaults
        }
    }
    const getWeekDateRange = (dateStr, datesCount = 7) => {
        const week = []
        for (let i = 1; i <= datesCount; i++) {
            const day = moment(dateStr).day(i).format(formatDates.defaultDate)
            week.push(day)
        }
        return week
    }
    const getDateRange = (dateStr, datesCount = 7) => {
        const week = []
        for (let i = 0; i <= datesCount - 1; i++) {
            const startDay = new Date(dateStr)
            const newDay = startDay.getDate() + i
            const newDate = new Date(startDay.setDate(newDay))
            const year = newDate.getFullYear()
            const month = ('0' + (newDate.getMonth() + 1)).slice(-2)
            const day = ('0' + newDate.getDate()).slice(-2)
            week.push(`${year}-${month}-${day}`)
        }
        return week
    }
    export default {
        data: () => ({
            designators: ['AM', 'PM', 'None'],
            clinics: [],
            clinicStatuses: {},
            weekDates: getWeekDateRange(new Date().toISOString().substr(0, 10), defaultSettings().days),
            calendarData: [],
            convertedData: {},
            employeeDataLoading: false,
            selectedItems: [],
            roleId: [],
            chosenEmployee: null,
            employeeSearchLoading: false,
            settingsModal: false,
            tableSettings: defaultSettings(),
            defaultSettings: defaultSettings(),
            selectInputConts: selectInputAutocomplete,
            searchInputAutocomplete: {
                [selectInputAutocomplete.roles]: null
            }
        }),
        filters: {
            dayFormat (dateStr) {
                return moment(dateStr).format('MMM D, ddd')
            }
        },
        computed: {
            ...mapGetters(['getAllMainRota', 'getClinicSurgeries', 'getOnlyClinics', 'getMainRotaSelected']),
            employees () {
                return this.$store.getters.getEmployeesSorted
            },
            clinicSurgeries () {
                return this.getClinicSurgeries(this.formClinicId)
            },
            clinicExpandDisabled () {
                return !this.roleId
            },
            roleList () {
                return this.$store.getters.getRoles
            },
            showCalendar () {
                return this.calendarData.length > 0 && this.currentEmployee
            },
            monthTitle () {
                return getMonthName(this.dateFrom)
            },
            selectedRotaItems () {
                return this.getMainRotaSelected
            },
            clinicsData () {
                const clinics = this.$store.getters.getAvailableClinics.concat()
                clinics.splice(0, 0, {
                    id: 'off',
                    isExpand: false,
                    loading: false,
                    title: 'Off'
                }, {
                    id: 'spare',
                    isExpand: false,
                    loading: false,
                    title: 'Spare'
                })
                return clinics.map(clinic => {
                    const clinicStatus = this.clinicStatuses[clinic.id]
                    if (clinicStatus) {
                        clinic.loading = clinicStatus.loading
                        clinic.isExpand = clinicStatus.isExpand
                    } else {
                        clinic.loading = false
                        clinic.isExpand = false
                    }
                    return clinic
                })
            },
            rotaData () {
                const rota = this.getAllMainRota
                const data = this.weekDates.reduce((acc, date) => {
                    acc[date] = rota.filter(rotaItem => date === rotaItem.date)
                    acc[date] = acc[date].reduce((acc, item) => {
                        item.records.forEach((record) => {
                            record.employeeId = item.employeeId
                            record.employeeName = item.employeeName
                            record.date = item.date
                            if (record.status === ROTA_STATUSES.off) {
                                setData('clinic', 'off', record)
                            } else if (record.status === ROTA_STATUSES.spare) {
                                setData('clinic', 'spare', record)
                            } else {
                                if (record.surgeryId) {
                                    setData('surgeries', record.surgeryId, record)
                                } else {
                                    setData('clinic', record.clinicId, record)
                                }
                            }
                        })
                        return acc
                        function setData (type = 'clinic', id = 0, item = {}) {
                            if (id) {
                                if (acc[type][id]) {
                                    if (!acc[type][id]) {
                                        acc[type][id] = {}
                                    }
                                    if (!acc[type][id][item.employeeId]) {
                                        acc[type][id][item.employeeId] = []
                                    }
                                    acc[type][id][item.employeeId].push(item)
                                } else {
                                    acc[type][id] = {}
                                    acc[type][id][item.employeeId] = []
                                    acc[type][id][item.employeeId].push(item)
                                }
                            } else {
                                if (acc[type][item.employeeId]) {
                                    acc[type][item.employeeId].push(item)
                                } else {
                                    acc[type] = {}
                                    acc[type][item.employeeId] = []
                                    acc[type][item.employeeId].push(item)
                                }
                            }
                        }
                    }, {
                        clinic: {},
                        surgeries: {}
                    })
                    return acc
                }, {})
                return data
            },
            clinicOpeningsData () {
                const openingDays = this.$store.getters.getOpeningDays
                if (!openingDays) {
                    return {}
                }
                return this.weekDates.reduce((acc, date) => {
                    acc[date] = openingDays.filter(item => date === item.date)
                    acc[date] = acc[date].reduce((acc, item) => {
                        if (item.surgeryId) {
                            setData('surgeries', item.surgeryId, item)
                        } else {
                            setData('clinic', item.clinicId, item)
                        }
                        return acc
                        function setData (type, id, item) {
                            if (acc[type][id]) {
                                acc[type][id].push(item)
                            } else {
                                acc[type][id] = [item]
                            }
                        }
                    }, {
                        clinic: {},
                        surgeries: {}
                    })
                    return acc
                }, {})
            },
            reloadDisabled () {
                return this.clinicsNeedsLoad.length === 0
            },
            clinicsNeedsLoad () {
                const clinics = []
                for (const [key, value] of Object.entries(this.clinicStatuses)) {
                    if (value.isExpand && key) {
                        if (+key || key === 'spare' || key === 'off') {
                            clinics.push(key === 'spare' || key === 'off' ? key : +key)
                        }
                    }
                }
                return clinics
            },
            isApprovePermission () {
                return this.$store.getters.getAllUserPermissions.includes(100)
            },
            isPermissionForRotaNotes () {
                return this.$store.getters.getAllUserPermissions.includes(100)
            }
        },
        methods: {
            ...mapActions(['fetchMainRota', 'fetchEmployees', 'reassignPersonalRota', 'showAlert', 'updateUserRotaNote']),
            async searchByEmployee () {
                this.employeeSearchLoading = true
                this.clinicStatuses = {}
                await this.$store.dispatch('cleanMainRota')
                await this.fetchCalendarData(this.clinicsNeedsLoad)
                if (this.chosenEmployee) {
                    this.clinicsData.forEach((item) => {
                        this.$set(this.clinicStatuses, item.id, {
                            isExpand: true,
                            loading: false
                        })
                    })
                }
                this.employeeSearchLoading = false
            },
            weekNav (action) {
                const daysToShow = this.tableSettings.days
                const weekDates = this.weekDates.concat()
                const now = action === 'next' ? new Date(weekDates.pop()) : new Date(weekDates[0])
                const increaseValue = action === 'next' ? 1 : -daysToShow
                const nextWeekDate = new Date(now.setDate(now.getDate() + increaseValue)).toISOString().substr(0, 10)
                this.weekDates = getDateRange(nextWeekDate, daysToShow)
                this.fetchAllCalendarData()
            },
            fetchAllCalendarData () {
                if (this.clinicsNeedsLoad.length > 0) {
                    this.fetchCalendarData(this.clinicsNeedsLoad)
                }
            },
            async clinicClickHandler (clinic) {
                if (this.clinicExpandDisabled) {
                    animateComponent(this.$refs.roleSelect, 'shake-animation')
                    this.showAlert({
                        text: 'Please choose role!',
                        type: 'info'
                    })
                    return false
                }
                const id = clinic.id
                if (this.clinicStatuses[id]) {
                    this.clinicStatuses[id].isExpand = !this.clinicStatuses[id].isExpand
                } else {
                    this.$set(this.clinicStatuses, id, {
                        isExpand: true,
                        loading: typeof id === 'number'
                    })
                }
                if ((this.clinicStatuses[id].isExpand && typeof id === 'number') ||
                    (this.clinicStatuses[id].isExpand && id === 'spare') ||
                    (this.clinicStatuses[id].isExpand && id === 'off')) {
                    await this.fetchCalendarData(id)
                }
            },
            clearSelectedList () {
                this.$store.commit('CLEAR_SELECTED_MAIN_ROTA')
            },
            unSelectItem (item) {
                this.$store.commit('UNSELECT_MAIN_ROTA', item)
            },
            onNoteSuccess (text) {
                this.$store.dispatch('showAlert', {
                    type: 'success',
                    text
                })
                this.reloadMainRota()
            },
            async reloadMainRota () {
                this.$store.dispatch('cleanMainRota')
                this.fetchAllCalendarData()
            },
            async roleChangeHandler (isResetInput, typeSearch) {
                if (isResetInput) {
                    this.changeOption(typeSearch)
                }
                this.fetchAllCalendarData()
            },
            async fetchCalendarData (clinicId) {
                const tempId = []
                if (clinicId === 'spare' || clinicId === 'off' || clinicId[0] === 'spare' || clinicId[0] === 'off') {
                    const firstClinicId = this.clinicsData.find(clinic => typeof clinic.id === 'number').id
                    tempId.push(firstClinicId)
                }
                const clinicIds = Array.isArray(clinicId) ? clinicId : [clinicId]
                clinicIds.forEach(id => {
                    this.clinicStatuses[id].loading = true
                })
                const weekDates = this.weekDates.concat()
                this.calendarData = await this.fetchMainRota({
                    clinicIds: tempId.length ? tempId : clinicIds,
                    RoleIds: this.roleId,
                    from: weekDates[0],
                    to: weekDates.pop(),
                    GroupId: this.$store.getters.getUserActiveGroup,
                    EmployeeIds: this.chosenEmployee
                })
                clinicIds.forEach(id => {
                    this.clinicStatuses[id].loading = false
                })
            },
            closeSettingsModal () {
                this.settingsModal = false
            },
            applySettings (settings) {
                this.tableSettings = settings
                const datesShowed = this.weekDates.length
                this.weekDates = getDateRange(this.weekDates[0], this.tableSettings.days)
                if (this.tableSettings.days > datesShowed) {
                    this.fetchAllCalendarData()
                }
            },
            successSubmit (infoText = 'Success!') {
                this.clearSelectedList()
                this.showAlert({
                    text: infoText,
                    type: 'info'
                })
                this.$store.dispatch('cleanMainRota')
                this.fetchAllCalendarData()
            },
            changeOption (typeSearch) {
                this.searchInputAutocomplete[typeSearch] = ''
            }
        },
        async mounted () {
            this.$store.dispatch('fetchRoles')
            const { rotaRoleIds } = await this.$store.dispatch('fetchSettings')
            if (rotaRoleIds.length) this.roleId = rotaRoleIds
            /* await this.$store.dispatch('fetchClinicsByGroupId', {
                 id: this.$store.getters.getUserActiveGroup
             }) */
        },
        async beforeDestroy () {
            await this.$store.dispatch('cleanMainRota')
            this.clearSelectedList()
        },
        components: {
            AddRotaRecord,
            MainAppBar,
            RotaTransferForm,
            MainCalendarDay,
            MainRotaSettingsDialog,
            RoleColorsLegend,
            RotaNote
        }
    }
</script>

<style lang="scss" deep>
    .sticky-td-desktop {
        top: 0
    }
    .table-wrapper {
        .v-data-table__wrapper {
            overflow-y: auto;
            height: 63vh;
            width: 99%;
            position: relative;
            z-index: 1;

            @media (max-height: 800px){
                height: 52vh;
            }

            @media (min-height: 900px){
                height: 66vh;
            }

            @media (min-height: 1080px){
                height: 70vh;
            }
        }
    }
    .sticky-th {
        position: sticky;
        z-index: 5;
        background-color: #ffffff;

        &--days {
            top: 0;
        }

        &--times {
            top: 32px;
        }
    }
    .v-app-bar--is-scrolled {
        ~ .main-rota-container {
            .main-rota-head {
                position: sticky;
                top: 64px;
                z-index: 100;
                background-color: #ffffff;
            }
        }
    }
    .table-bordered {
        table {
            td {
                padding-left: 0;
                padding-right: 0;
                vertical-align: top;
                border: 1px solid rgba(0, 0, 0, .12);

                &:first-child {
                    width: 150px;
                    padding: 0 10px;
                }

                &.sticky-td {
                    position: sticky;
                    left: 0;
                    z-index: 1;
                    background-color: #fff;
                }
            }

            tr {
                &.expanded-content {
                    td {
                        background-color: #fff;
                    }
                }

                &.expandable-tr {
                    cursor: pointer;
                    background-color: #fff;
                    font-weight: bold;

                    &:hover {
                        td {
                            background-color: #e9e9e9 !important;
                        }
                    }

                    &.disabled-tr {
                        opacity: 0.5;
                    }

                    &.expanded {
                        td {
                            background-color: #e9e9e9 !important;
                        }
                    }
                }
            }

            thead {
                th {
                    padding-left: 10px;
                    padding-right: 10px;
                    color: #000;
                    border: 1px solid rgba(0, 0, 0, .12);
                    &.sticky-td {
                        position: sticky;
                        left: 0;
                        z-index: 6;
                        background-color: #fff;
                    }
                }

                tr {
                    transition: all .3s;

                    th {
                        height: 20px;
                        padding-top: 0;
                        padding-bottom: 0;
                    }

                    &:first-child {
                        th {
                            padding: 5px !important;
                            height: 20px;
                        }
                    }

                    &:last-child {
                        th {
                            border-bottom: 0;
                        }
                    }
                }
            }
        }
    }

    .shake-animation {
        animation: shake 1s linear;
    }

    @keyframes shake {
        0%, 100% {
            -webkit-transform: translate3d(0, 0, 0);
            transform: translate3d(0, 0, 0);
        }

        10%, 30%, 50%, 70%, 90% {
            -webkit-transform: translate3d(-10px, 0, 0);
            transform: translate3d(-10px, 0, 0);
        }

        20%, 40%, 60%, 80% {
            -webkit-transform: translate3d(10px, 0, 0);
            transform: translate3d(10px, 0, 0);
        }
    }
</style>
